// Mirror cameraAngle's azimuth circle so dragging on the LightStage maps to familiar
// compass directions for the light source.
export const AZIMUTH_STOPS = [
  { label: 'Front', value: 0 },
  { label: 'Front-right', value: 45 },
  { label: 'Right', value: 90 },
  { label: 'Back-right', value: 135 },
  { label: 'Back', value: 180 },
  { label: 'Back-left', value: -135 },
  { label: 'Left', value: -90 },
  { label: 'Front-left', value: -45 },
] as const;

export const ELEVATION_STOPS = [
  { label: 'Below', value: -30 },
  { label: 'Eye level', value: 0 },
  { label: 'Above', value: 30 },
  { label: 'High', value: 60 },
  { label: 'Overhead', value: 85 },
] as const;

export type LightTypeId =
  | 'sun'
  | 'moon'
  | 'candle'
  | 'fireplace'
  | 'studioSoftbox'
  | 'window'
  | 'neon'
  | 'overcast'
  | 'solidColor';

export type EmissionId = 'ambient' | 'direct' | 'spotlight' | 'softbox';

export type DistanceId = 'close' | 'mid' | 'far';

export type ConeWidthId = 'narrow' | 'medium' | 'wide';

export type BrightnessId = 'dim' | 'soft' | 'balanced' | 'bright' | 'blazing';

/** Inner shape for the LightStage glyph. Glow halo + brightness scaling are shared across shapes. */
export type GlyphShape = 'circle' | 'sun' | 'moon' | 'flame' | 'neon';

export type LightTypeMeta = {
  id: LightTypeId;
  label: string;
  /** Default fill for the LightStage glyph and inline swatch. */
  glyphColor: string;
  /** When true, the side panel reveals an inline colour picker for `solidColor`. */
  acceptsColor?: boolean;
  /** Per-type glyph shape rendered on the LightStage. Defaults to 'circle' when omitted. */
  glyphShape?: GlyphShape;
  /** Hide the Emission select for this light type — its synthesizer doesn't read it. */
  hidesEmission?: boolean;
  /** Hide the Distance slider — the source is implicitly fixed (e.g. astronomical). */
  hidesDistance?: boolean;
  /** Hide every position control (azimuth, elevation, distance) — type is omnidirectional. */
  hidesPosition?: boolean;
  /**
   * Subset of `EMISSION_TYPES` allowed for this light type. When omitted, every emission is
   * allowed. The side panel filters the dropdown to this list and resets `emissionId` when the
   * user picks a light type whose current emission is no longer valid.
   * Ignored when `hidesEmission` is true.
   */
  allowedEmissions?: readonly EmissionId[];
};

export const DEFAULT_SOLID_COLOR = '#FFD27A';
// Default neon tube colour used by the LightStage glyph and the prompt synthesizer when the
// user hasn't picked a custom colour. Mirrors the `neon` entry in `LIGHT_TYPES`.
export const DEFAULT_NEON_TUBE_COLOR = '#E84BC8';
// Sun's "afternoon" tint — also the value `LIGHT_TYPES.sun` advertises as a metadata default,
// though `derivations.ts` always overrides it with the live `SUN_GLYPH_COLORS[sunTimeOfDayId]`
// lookup. Kept as a single source of truth so both references can't drift.
export const DEFAULT_SUN_GLYPH_COLOR = '#F4C752';

export const LIGHT_TYPES: readonly LightTypeMeta[] = [
  {
    glyphColor: DEFAULT_SUN_GLYPH_COLOR,
    glyphShape: 'sun',
    hidesDistance: true,
    hidesEmission: true,
    id: 'sun',
    label: 'Sun',
  },
  { glyphColor: '#E0E8F2', glyphShape: 'moon', hidesDistance: true, hidesEmission: true, id: 'moon', label: 'Moon' },
  {
    allowedEmissions: ['ambient', 'direct'],
    glyphColor: '#F2A035',
    glyphShape: 'flame',
    id: 'candle',
    label: 'Candlelight',
  },
  {
    allowedEmissions: ['ambient', 'direct'],
    glyphColor: '#E8642A',
    glyphShape: 'flame',
    id: 'fireplace',
    label: 'Fireplace',
  },
  { allowedEmissions: ['direct', 'softbox'], glyphColor: '#F8F8F8', id: 'studioSoftbox', label: 'Studio softbox' },
  { allowedEmissions: ['ambient', 'direct', 'softbox'], glyphColor: '#E8EFF7', id: 'window', label: 'Window light' },
  {
    acceptsColor: true,
    allowedEmissions: ['ambient', 'direct'],
    glyphColor: DEFAULT_NEON_TUBE_COLOR,
    glyphShape: 'neon',
    id: 'neon',
    label: 'Neon',
  },
  { glyphColor: '#D4DAE0', hidesEmission: true, hidesPosition: true, id: 'overcast', label: 'Overcast daylight' },
  { acceptsColor: true, glyphColor: DEFAULT_SOLID_COLOR, id: 'solidColor', label: 'Solid color' },
];

export const EMISSION_TYPES: readonly { id: EmissionId; label: string }[] = [
  { id: 'ambient', label: 'Ambient' },
  { id: 'direct', label: 'Direct' },
  { id: 'spotlight', label: 'Spotlight' },
  { id: 'softbox', label: 'Diffuse softbox' },
];

export const DISTANCE_STOPS: readonly { id: DistanceId; label: string }[] = [
  { id: 'close', label: 'Close' },
  { id: 'mid', label: 'Mid' },
  { id: 'far', label: 'Far' },
];

export const CONE_WIDTHS: readonly { id: ConeWidthId; label: string }[] = [
  { id: 'narrow', label: 'Narrow' },
  { id: 'medium', label: 'Medium' },
  { id: 'wide', label: 'Wide' },
];

export const BRIGHTNESS_STOPS: readonly { id: BrightnessId; label: string }[] = [
  { id: 'dim', label: 'Dim' },
  { id: 'soft', label: 'Soft' },
  { id: 'balanced', label: 'Balanced' },
  { id: 'bright', label: 'Bright' },
  { id: 'blazing', label: 'Blazing' },
];

// Sun-only param: time-of-day re-frames the light's colour temperature, shadow length, and intensity.
// Surfaced in the side panel only when light type is `sun`.
export type SunTimeOfDayId = 'morning' | 'midday' | 'goldenHour' | 'afternoon' | 'dusk' | 'blueHour';

export const SUN_TIME_OF_DAY: readonly { id: SunTimeOfDayId; label: string }[] = [
  { id: 'morning', label: 'Morning' },
  { id: 'midday', label: 'Midday' },
  { id: 'goldenHour', label: 'Golden hour' },
  { id: 'afternoon', label: 'Afternoon' },
  { id: 'dusk', label: 'Dusk' },
  { id: 'blueHour', label: 'Blue hour' },
];

// Per-time-of-day glyph tint for the LightStage when the active light type is `sun`. Visually
// reinforces the sun's colour-temperature shift across the day.
export const SUN_GLYPH_COLORS: Record<SunTimeOfDayId, string> = {
  afternoon: DEFAULT_SUN_GLYPH_COLOR, // warm yellow (preserves the previous default)
  blueHour: '#5B7FB0', // deep cool blue
  dusk: '#E85F35', // deep red-orange
  goldenHour: '#F4A235', // warm amber
  midday: '#FFFAEB', // bright white-yellow
  morning: '#F0D478', // cool gold
};

export const DEFAULT_AZIMUTH_IDX = 7; // Front-left — flattering default for portraits
export const DEFAULT_ELEVATION_IDX = 2; // Above
export const DEFAULT_DISTANCE_ID: DistanceId = 'mid';
export const DEFAULT_BRIGHTNESS_ID: BrightnessId = 'balanced';
export const DEFAULT_LIGHT_TYPE_ID: LightTypeId = 'sun';
export const DEFAULT_EMISSION_ID: EmissionId = 'direct';
export const DEFAULT_CONE_WIDTH_ID: ConeWidthId = 'medium';
// "Afternoon" preserves the prior `describeLightType('sun')` phrasing for users who don't change it.
export const DEFAULT_SUN_TIME_OF_DAY_ID: SunTimeOfDayId = 'afternoon';
